<template>
  <DropDown
    class="w-full"
    wrapper-class="border-main-solid! bg-transparent! w-full! top-0!"
    @open="emit('toggle', true)"
    @close="emit('toggle', false)"
  >
    <template #activator="{ open, close, isOpen }">
      <button
        data-testid="sidebar-switcher-toggle"
        class="mb-5 flex w-full items-center justify-between gap-2 text-left text-subtle dark-theme"
        @click="isOpen ? close() : open()"
      >
        <div class="flex gap-2">
          <img v-if="me" class="ml-2 h-10 w-10 rounded-sm" :src="me.profileImage" />
          <div>
            <p v-if="me?.partnerCompany" class="max-w-[129px] overflow-hidden text-ellipsis whitespace-nowrap">
              {{ me.partnerCompany.displayName || me.partnerCompany.name }}
            </p>
            <p class="text-body-s text-subtlest">{{ meStore.userName }}</p>
          </div>
        </div>
        <MdiSvg class="text-subtlest" :class="{ 'rotate-180': !!isOpen }" :path="mdiChevronDown" />
      </button>
    </template>
    <template #default>
      <div class="h-full bg-surface text-left dark-theme">
        <p class="w-40 px-4 py-2 text-body-s-bold text-subtle">{{ t('SideBar.switchAccount') }}</p>

        <button
          v-for="pc in sortedPartnerCompanies"
          :key="pc.id"
          :data-testid="'sidebar-switcher-company-' + pc.id"
          class="state-layer-neutral flex w-full items-center justify-between gap-2 px-4 py-2 text-left text-subtle dark-theme"
          :class="{ 'bg-state-hover-neutral': isCurrentPartnerCompany(pc.id) }"
          @click="switchTo(pc.id)"
        >
          <div class="flex gap-2">
            <img v-if="pc.webLogoUrl" class="h-10 w-10 rounded-sm object-contain" :src="pc.webLogoUrl" />
            <img v-else class="h-10 w-10 rounded-sm" src="/assets/images/placeholder-logo.svg" />
            <div>
              <p
                class="overflow-hidden text-ellipsis whitespace-nowrap"
                :class="{ 'max-w-[114px]': isCurrentPartnerCompany(pc.id), 'max-w-[140px]': !isCurrentPartnerCompany(pc.id) }"
              >
                {{ pc.displayName }}
              </p>
              <p
                class="overflow-hidden text-body-s text-ellipsis whitespace-nowrap text-subtlest"
                :class="{ 'max-w-[114px]': isCurrentPartnerCompany(pc.id), 'max-w-[140px]': !isCurrentPartnerCompany(pc.id) }"
              >
                {{ meStore.userName }}
              </p>
            </div>
          </div>
          <CheckIcon v-if="isCurrentPartnerCompany(pc.id)" class="lucide-icon-s text-primary" />
        </button>
        <HorizontalDivider />
        <a
          data-testid="sidebar-logout"
          class="state-layer-neutral flex w-full cursor-pointer items-center px-4 py-2 text-body-m text-subtle"
          :href="logoutUrl(locale)"
          @click="signOut()"
        >
          <LogOutIcon class="mr-2 lucide-icon-s text-subtle" />
          <span>{{ t('SideBar.logout') }}</span>
        </a>
      </div>
    </template>
  </DropDown>
</template>
<script setup lang="ts">
import { mdiChevronDown } from '@mdi/js'
import { DropDown, MdiSvg } from '@ramp106/omrjs-core-ui'
import { CheckIcon, LogOutIcon } from '@ramp106/omrjs-core-ui/icons'
import { resetIdentity } from '@ramp106/omrjs-tracking-reviews'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'

import { logoutUrl } from '@/config/env'
import type { PartnerCompany, MeQuery } from '@/gql/myomr'
import { usePartnerCompaniesQuery } from '@/gql/myomr'
import { toGlobalId } from '@/helpers/globalIdHelper'
import { getPartnerSwitchRedirect } from '@/services/partnerCompanySwitchRedirect'
import { resetAllStores, useLocaleStore, useMeStore } from '@/stores'

const props = defineProps<{
  me: MeQuery['me'] | undefined
}>()

const emit = defineEmits<{
  (e: 'toggle', value: boolean): void
}>()

const meStore = useMeStore()
const { locale } = useLocaleStore()
const { t } = useI18n()
const route = useRoute()

function signOut() {
  resetIdentity()
  resetAllStores()
}

const partnerCompanyIds = computed(() => {
  return props.me?.roles
    .filter((role) => role.resourceType === 'PartnerCompany' && role.resourceId)
    .map((role) => toGlobalId('PartnerCompany', role.resourceId as string))
    .filter((id, index, array) => array.indexOf(id) === index)
})

const { result } = usePartnerCompaniesQuery({ partnerCompanyIds: partnerCompanyIds.value ?? [] })
const partnerCompanies = computed(() => (result.value?.nodes as PartnerCompany[]) || [])

const sortedPartnerCompanies = computed(() =>
  [...partnerCompanies.value].sort((a, b) => (a.displayName ?? '').localeCompare(b.displayName ?? '')),
)

function isCurrentPartnerCompany(companyId: string) {
  return props.me?.partnerCompany && props.me.partnerCompany.id === companyId
}

function switchTo(id: string) {
  meStore.setCurrentPartnerCompanyGlobalId(id)
  const redirectTo = getPartnerSwitchRedirect(route.path)
  if (redirectTo) {
    window.location.assign(redirectTo)
    return
  }
  location.reload()
}
</script>
